import React, { useEffect, useState } from 'react';
import { Trans } from '@lingui/react/macro';

import clsx from 'clsx';
import {
  AlertCircle,
  ArrowRight,
  ArrowUpRight,
  BarChart3,
  CheckCircle,
  FileText,
  HelpCircle,
  Info,
  Lock,
  MessageSquare,
  RefreshCw,
  Star,
} from 'lucide-react';
import { toast } from 'sonner';

import { useTranscription } from '~/hooks/useTranscription';
import type { SpeechAnalysis } from '~/routes/api.analyze';
import { formatDateForFilename } from '~/utils/date';
import { createClientLogger } from '~/utils/logger.client';
import { getThumbnailUrl } from '~/utils/thumbnails';

import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from './ui/card';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import VideoViewer from './VideoViewer';
import HLSVideoPlayer from './HLSVideoPlayer';
import FastVideoPlayer from './FastVideoPlayer';
import VideoDebug from './VideoDebug';

// Import extracted components
import {
  AudioSetting,
  VideoSetting,
  VideoAnalysisDisplay,
  ScriptImprover,
  DevAudioPlayer,
  MobileErrorBoundary,
  getMetricExplanation,
  getSeverityColors,
  isMobileDevice,
  IS_VIDEO_DISABLED,
  HiringManagerSummary,
  ReadinessScore,
  NextAttemptFocus,
} from './Analysis';

// Lazy logger creation to avoid SSR issues with mobile optimization
let logger: ReturnType<typeof createClientLogger> | null = null;
const getLogger = () => {
  if (!logger && typeof window !== 'undefined') {
    try {
      logger = createClientLogger('AIAnalysis');
    } catch (error) {
      // Fallback for mobile devices with memory constraints
      console.warn('Failed to create logger, using console fallback:', error);
      return {
        info: (msg: string, data?: any) => console.info(`[AIAnalysis] ${msg}`, data?.metadata || ''),
        warn: (msg: string, data?: any) => console.warn(`[AIAnalysis] ${msg}`, data?.metadata || ''),
        error: (msg: string, data?: any) => console.error(`[AIAnalysis] ${msg}`, data?.metadata || ''),
        debug: (msg: string, data?: any) => console.debug(`[AIAnalysis] ${msg}`, data?.metadata || ''),
      };
    }
  }
  return logger || {
    info: (msg: string, data?: any) => console.info(`[AIAnalysis] ${msg}`, data?.metadata || ''),
    warn: (msg: string, data?: any) => console.warn(`[AIAnalysis] ${msg}`, data?.metadata || ''),
    error: (msg: string, data?: any) => console.error(`[AIAnalysis] ${msg}`, data?.metadata || ''),
    debug: (msg: string, data?: any) => console.debug(`[AIAnalysis] ${msg}`, data?.metadata || ''),
  };
};

interface QuestionFeedback {
  question: string;
  strengths: string[];
  weaknesses: string[];
  suggestion: string;
}

interface AIAnalysisProps {
  videoBlob: Blob | null;
  analysisData?: {
    // Required fields (present in both full and partial analysis)
    fillerWords: string[];
    score?: number | null;
    personalizedTips: string[];
    speechPace?: string | null;
    clarity?: string | null;
    sentiment?: string | null;
    keyPointsDetected: string[];
    questionFeedback?: QuestionFeedback[];
    // Optional fields (only present in full database analysis)
    id?: string;
    transcription?: string;
    improvedScript?: string | null;
    status?: string;
    intentCategory?: string | null;
    intentDescription?: string | null;
    files?: Array<{
      id: string;
      name: string;
      key: string;
      size: number;
      type: string;
      duration?: number | null;
      thumbnailKey?: string | null;
      createdAt: Date;
      updatedAt: Date;
      isTemporary: boolean;
      isEncrypted: boolean;
      encryptionIv?: string | null;
      encryptionTag?: string | null;
      intentCategory?: string | null;
      intentDescription?: string | null;
    }>;
  } | null;
  isAnalyzing: boolean;
  videoUrl?: string;
  // classic: existing behavior with large video on top
  // data-first: summary + sections; video expected to be rendered elsewhere (e.g., sidebar)
  layout?: 'classic' | 'data-first';
  // Optional thumbnail key override (for audio-first workflow where thumbnail is on video file)
  thumbnailKey?: string | null;
  // Primary file ID to use for API calls (replaces analysisData.file.id)
  primaryFileId?: string;
  // Read-only mode disables edit/save buttons (for shared views)
  readOnly?: boolean;
  // Direct thumbnail URL for demo mode (bypasses R2 thumbnail lookup)
  demoThumbnailUrl?: string;
  // Report access level for credit-gated reports
  reportAccess?: 'full' | 'partial' | 'locked';
  // Number of credits user currently has (determines which paywall CTA to show)
  practicesRemaining?: number;
  // Whether an unlock action is currently in flight
  isUnlocking?: boolean;
  // Callback when user clicks "Unlock full report (1 credit)" (has credits)
  onUnlockWithCredit?: () => void;
  // Callback when user clicks "Get Credits" (no credits)
  onGetCreditsClick?: () => void;
  // Backward compat: fallback for onGetCreditsClick
  onUnlockClick?: () => void;
}

// Legacy interface alias for backward compatibility (kept for reference)
interface _LegacyAIAnalysisProps {
  videoBlob: Blob | null;
  analysisData?: {
    id: string;
    transcription: string;
    improvedScript?: string | null;
    status: string;
    fillerWords: string[];
    score?: number | null;
    personalizedTips: string[];
    speechPace?: string | null;
    clarity?: string | null;
    sentiment?: string | null;
    keyPointsDetected: string[];
    intentCategory?: string | null;
    intentDescription?: string | null;
    questionFeedback?: QuestionFeedback[];
    files: Array<{
      id: string;
      name: string;
      key: string;
      size: number;
      type: string;
      duration?: number | null;
      thumbnailKey?: string | null;
      createdAt: Date;
      updatedAt: Date;
      isTemporary: boolean;
      isEncrypted: boolean;
      encryptionIv?: string | null;
      encryptionTag?: string | null;
      intentCategory?: string | null;
      intentDescription?: string | null;
    }>;
  } | null;
  isAnalyzing: boolean;
  videoUrl?: string;
  // classic: existing behavior with large video on top
  // data-first: summary + sections; video expected to be rendered elsewhere (e.g., sidebar)
  layout?: 'classic' | 'data-first';
  // Optional thumbnail key override (for audio-first workflow where thumbnail is on video file)
  thumbnailKey?: string | null;
  // Primary file ID to use for API calls (replaces analysisData.file.id)
  primaryFileId?: string;
}

function PaywallBanner({
  practicesRemaining,
  isUnlocking,
  onUnlockWithCredit,
  onGetCredits,
}: {
  practicesRemaining: number;
  isUnlocking: boolean;
  onUnlockWithCredit?: () => void;
  onGetCredits?: () => void;
}) {
  const hasCredits = practicesRemaining > 0;

  return (
    <Card className="border-2 border-brand-gold/30 bg-gradient-to-r from-brand-gold/5 to-brand-dark-blue/5">
      <CardContent className="py-10 text-center">
        <Lock className="w-10 h-10 text-brand-dark-blue/30 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-brand-dark-blue mb-2">
          <Trans>Unlock Your Full Report</Trans>
        </h3>
        <p className="text-brand-dark-blue/60 text-sm mb-6 max-w-md mx-auto">
          {hasCredits ? (
            <Trans>Use one practice credit to access your detailed speech analysis, improvement tips, and more</Trans>
          ) : (
            <Trans>Get practice credits to access your detailed speech analysis, improvement tips, and more</Trans>
          )}
        </p>
        <div className="flex flex-col items-center gap-3">
          {hasCredits && onUnlockWithCredit ? (
            <button
              onClick={onUnlockWithCredit}
              disabled={isUnlocking}
              className="bg-brand-gold text-white px-8 py-3 rounded-full font-semibold hover:bg-brand-gold/90 transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isUnlocking ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  <Trans>Unlocking...</Trans>
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  <Trans>Unlock</Trans>
                </>
              )}
            </button>
          ) : onGetCredits ? (
            <button
              onClick={onGetCredits}
              className="bg-brand-dark-blue text-white px-8 py-3 rounded-full font-semibold hover:bg-brand-dark-blue/90 transition-colors inline-flex items-center gap-2"
            >
              <Trans>Get Credits</Trans>
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : null}
          {/* {hasCredits && (
            <p className="text-xs text-brand-dark-blue/50">
              <Trans>You have {practicesRemaining} credit(s) remaining</Trans>
            </p>
          )} */}
          <a
            href="/demo"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-sm font-bold text-brand-dark-blue hover:text-brand-dark-blue/80 transition-colors"
          >
            <Trans>View full sample report</Trans>
            <ArrowUpRight className="w-4 h-4 ml-1" />
          </a>
        </div>
      </CardContent>
    </Card>
  );
}

function TeaserSection({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative max-h-[150px] overflow-hidden pointer-events-none select-none">
      <div className="blur-sm opacity-60">
        {children}
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-white" />
    </div>
  );
}

export default function AIAnalysis({
  videoBlob,
  analysisData: initialAnalysisData,
  isAnalyzing,
  videoUrl,
  layout = 'classic',
  thumbnailKey,
  primaryFileId,
  readOnly = false,
  demoThumbnailUrl,
  reportAccess = 'full',
  practicesRemaining = 0,
  isUnlocking = false,
  onUnlockWithCredit,
  onGetCreditsClick,
  onUnlockClick,
}: AIAnalysisProps) {
  const [internalVideoUrl, setInternalVideoUrl] = useState<string>('');
  const { getTranscriptionStatus } = useTranscription();
  const [transcriptId, setTranscriptId] = useState<string | null>(null);
  const [hasCelebrated, setHasCelebrated] = useState(false);
  const [analysisData, setAnalysisData] = useState(initialAnalysisData);
  const [componentError, setComponentError] = useState<string | null>(null);
  const [isMobile] = useState(() => isMobileDevice());
  const [errorTimeoutId, setErrorTimeoutId] = useState<NodeJS.Timeout | null>(null);

  // Function to set component error with 1-minute timeout
  const setComponentErrorWithTimeout = (error: string) => {
    setComponentError(error);
    
    // Clear any existing timeout
    if (errorTimeoutId) {
      clearTimeout(errorTimeoutId);
    }
    
    // Set new timeout to clear error after 1 minute
    const timeoutId = setTimeout(() => {
      setComponentError(null);
      setErrorTimeoutId(null);
      getLogger().info('AIAnalysis error auto-dismissed after 1 minute timeout');
    }, 60000); // 1 minute = 60,000ms
    
    setErrorTimeoutId(timeoutId);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (errorTimeoutId) {
        clearTimeout(errorTimeoutId);
      }
    };
  }, [errorTimeoutId]);

  // Update local state when initial data changes
  useEffect(() => {
    try {
      setAnalysisData(initialAnalysisData);
      setComponentError(null); // Clear any previous errors
      
      // Debug: Log what files we're receiving
      if (initialAnalysisData) {
        getLogger().info('AIAnalysis received analysisData', {
          metadata: {
            analysisId: (initialAnalysisData as any)?.id,
            totalFiles: (initialAnalysisData as any)?.files?.length || 0,
            files: (initialAnalysisData as any)?.files?.map((f: any) => ({
              id: f.id,
              key: f.key,
              type: f.type,
              thumbnailKey: f.thumbnailKey,
              isAudioEncrypted: f.key?.includes('-audio-encrypted'),
              isVideoType: f.type?.startsWith('video/'),
            })) || [],
          }
        });
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setComponentErrorWithTimeout(`Failed to process analysis data: ${errorMessage}`);
      getLogger().error('AIAnalysis failed to process initial data', {
        metadata: { 
          error: errorMessage,
          stack: error instanceof Error ? error.stack : undefined,
          analysisId: (initialAnalysisData as any)?.id
        }
      });
    }
  }, [initialAnalysisData]);

  // Poll for updated analysis data when files might be uploading
  useEffect(() => {
    try {
      if (!(analysisData as any)?.id) return;
      
      let pollInterval: NodeJS.Timeout;
      let pollCount = 0;
      const maxPolls = 30; // 5 minutes max (10s * 30)
      
      // Check if we should poll (analysis exists but might be missing files)
      const hasVideoFile = (analysisData as any)?.files?.some((f: any) => 
        f.key && !f.key.includes('-audio-encrypted') && 
        (f.type?.startsWith('video/') || f.key.includes('.mp4') || f.key.includes('.webm') || f.key.includes('.mov'))
      );
      
      const hasAudioFile = (analysisData as any)?.files?.some((f: any) => 
        f.key && f.key.includes('-audio-encrypted')
      );
      
      // SIMPLIFIED POLLING LOGIC:
      // Only poll if we have an audio file but NO video file (video might still be uploading in background)
      // Don't poll if we already have both files, or if we have no files at all
      const shouldPoll = hasAudioFile && !hasVideoFile && 
                        ((analysisData as any)?.files?.length || 0) > 0;
      
      if (shouldPoll) {
        getLogger().info('AIAnalysis starting file polling - analysis completed but missing files', {
          metadata: {
            analysisId: (analysisData as any).id,
            hasVideoFile,
            hasAudioFile,
            totalFiles: (analysisData as any)?.files?.length || 0
          }
        });
        
        pollInterval = setInterval(async () => {
          pollCount++;
          
          try {
            const response = await fetch(`/api/analysis/${(analysisData as any).id}/files`);
            if (response.ok) {
              const fileData = await response.json();
              getLogger().info('AIAnalysis file poll response', {
                metadata: {
                  analysisId: (analysisData as any).id,
                  hasVideoContent: fileData.hasVideoContent,
                  totalFiles: fileData.files.total,
                  pollCount
                }
              });
              
              // Check if we found new files (especially video files)
              const currentVideoFiles = (analysisData as any)?.files?.filter((f: any) => 
                f.key && !f.key.includes('-audio-encrypted') && 
                (f.type?.startsWith('video/') || f.key.includes('.mp4') || f.key.includes('.webm') || f.key.includes('.mov'))
              ).length || 0;
              
              const newVideoFiles = fileData.files.video.length;
              
              // If we now have video content, refresh the analysis data by reloading the page
              if (fileData.hasVideoContent && newVideoFiles > currentVideoFiles) {
                getLogger().info('AIAnalysis new video files detected, refreshing page');
                window.location.reload();
                return;
              }
              
              // If still no video files but we expect them, try to connect orphaned files
              if (!fileData.hasVideoContent && fileData.files.audio.length > 0) {
                getLogger().info('AIAnalysis no video files found, checking for orphaned files to connect');
                
                try {
                  // Try to connect orphaned video files using the existing API
                  const audioFileId = fileData.files.audio[0]?.id;
                  if (audioFileId) {
                    // Check for recent orphaned video files for this user
                    const orphanedResponse = await fetch('/api/connect-orphaned-files', {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                      },
                      body: JSON.stringify({
                        analysisId: (analysisData as any).id,
                        audioFileId: audioFileId,
                      }),
                    });
                    
                    if (orphanedResponse.ok) {
                      const orphanedData = await orphanedResponse.json();
                      if (orphanedData.connected) {
                        getLogger().info('AIAnalysis successfully connected orphaned files, refreshing page');
                        window.location.reload();
                        return;
                      }
                    } else {
                      getLogger().info('AIAnalysis no orphaned files found to connect');
                    }
                  }
                } catch (orphanedError) {
                  const orphanedErrorMessage = orphanedError instanceof Error ? orphanedError.message : String(orphanedError);
                  getLogger().warn('AIAnalysis error checking for orphaned files', {
                    metadata: { error: orphanedErrorMessage }
                  });
                }
              }
            }
          } catch (error) {
            const errorMessage = error instanceof Error ? error.message : String(error);
            getLogger().warn('AIAnalysis file polling error', {
              metadata: { error: errorMessage, pollCount }
            });
          }
          
          if (pollCount >= maxPolls) {
            getLogger().info('AIAnalysis file polling timeout reached');
            clearInterval(pollInterval);
          }
        }, 10000); // Poll every 10 seconds
      }
      
      return () => {
        if (pollInterval) {
          clearInterval(pollInterval);
        }
      };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setComponentErrorWithTimeout(`File polling setup failed: ${errorMessage}`);
      getLogger().error('AIAnalysis file polling setup error', {
        metadata: { 
          error: errorMessage,
          stack: error instanceof Error ? error.stack : undefined,
          analysisId: (analysisData as any)?.id
        }
      });
    }
  }, [(analysisData as any)?.id, (analysisData as any)?.files?.length, (analysisData as any)?.status]);

  // Poll for transcription status
  const transcriptionQuery = getTranscriptionStatus(transcriptId);

  useEffect(() => {
    try {
      if (videoBlob) {
        const url = URL.createObjectURL(videoBlob);
        setInternalVideoUrl(url);

        return () => {
          URL.revokeObjectURL(url);
        };
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setComponentErrorWithTimeout(`Failed to create video URL: ${errorMessage}`);
      getLogger().error('AIAnalysis failed to create video URL', {
        metadata: { 
          error: errorMessage,
          stack: error instanceof Error ? error.stack : undefined,
          hasBlobData: !!videoBlob
        }
      });
    }
  }, [videoBlob]);

  // Confetti celebration when analysis is complete and first viewed
  useEffect(() => {
    try {
      if (hasCelebrated || isAnalyzing || !analysisData) return;
      
      // Only celebrate if we have meaningful analysis data
      const hasAnalysisContent = (analysisData as any)?.transcription || 
                                analysisData.sentiment || 
                                analysisData.speechPace || 
                                analysisData.clarity;
      
      if (!hasAnalysisContent) return;

      // Create unique key for this analysis to track if confetti was already shown
      const analysisId = (analysisData as any)?.id || primaryFileId;
      const celebrationKey = `confetti_shown_${analysisId}`;
      
      // Check if we've already shown confetti for this analysis
      if (typeof window !== 'undefined') {
        const alreadyCelebrated = localStorage.getItem(celebrationKey);
        if (alreadyCelebrated) {
          setHasCelebrated(true);
          return;
        }
      }
      
      setHasCelebrated(true);

      // Mark this analysis as celebrated in localStorage
      if (typeof window !== 'undefined') {
        localStorage.setItem(celebrationKey, 'true');
      }

      // Dynamically import to avoid SSR issues
      import('canvas-confetti')
        .then(({ default: confetti }) => {
          const shoot = (opts: Partial<import('canvas-confetti').Options>) => confetti({
            spread: 70,
            startVelocity: 45,
            ticks: 200,
            gravity: 0.7,
            scalar: 1,
            origin: { y: 0.3 },
            colors: ['#D4AF37', '#1e3a8a', '#10b981', '#ffffff'], // Brand gold, dark blue, success green, white
            ...opts,
          });

          // Staggered confetti bursts for a nice effect
          shoot({ particleCount: 120 });
          setTimeout(() => shoot({ particleCount: 100, scalar: 0.9 }), 150);
          setTimeout(() => shoot({ particleCount: 80, scalar: 0.8 }), 300);
        })
        .catch((confettiError) => {
          // Log confetti error but don't set component error (it's not critical)
          getLogger().warn('AIAnalysis confetti animation failed', {
            metadata: { 
              error: confettiError instanceof Error ? confettiError.message : String(confettiError)
            }
          });
        });
    } catch (error) {
      // Log celebration error but don't set component error (it's not critical)
      getLogger().warn('AIAnalysis celebration setup failed', {
        metadata: { 
          error: error instanceof Error ? error.message : String(error),
          analysisId: (analysisData as any)?.id
        }
      });
    }
  }, [hasCelebrated, isAnalyzing, analysisData, primaryFileId]);

  const effectiveVideoUrl = videoUrl || internalVideoUrl;
  const isPartial = reportAccess === 'partial';

  // Section visibility: which sections are visible at each access level
  type SectionAccess = 'free' | 'partial' | 'full';
  const isSectionVisible = (sectionAccess: SectionAccess): boolean => {
    if (reportAccess === 'full') return true;
    if (reportAccess === 'partial') return sectionAccess === 'free' || sectionAccess === 'partial';
    return sectionAccess === 'free'; // locked
  };

  // Ordered sections with their minimum access level
  const sectionOrder: { key: string; access: SectionAccess }[] = [
    { key: 'fillerWords', access: 'free' },
    { key: 'transcript', access: 'partial' },
    { key: 'speechAnalysis', access: 'partial' },
    { key: 'questionFeedback', access: 'full' },
    { key: 'videoSetting', access: 'full' },
    { key: 'audioSetting', access: 'full' },
  ];

  // First locked section to use as teaser below the paywall
  const firstLockedSection = sectionOrder.find(s => !isSectionVisible(s.access));

  // const handleDownload = () => {
  //   if (!videoBlob && !videoUrl) return;

  //   let downloadUrl = '';
  //   if (videoBlob) {
  //     downloadUrl = URL.createObjectURL(videoBlob);
  //   } else if (videoUrl) {
  //     downloadUrl = videoUrl;
  //   }

  //   const a = document.createElement('a');
  //   a.href = downloadUrl;
  //   a.download = `speech-practice-${formatDateForFilename()}.webm`;
  //   document.body.appendChild(a);
  //   a.click();

  //   document.body.removeChild(a);
  //   if (videoBlob && downloadUrl) {
  //     URL.revokeObjectURL(downloadUrl);
  //   }

  //   toast.success('Video downloaded successfully');
  // };

  // Helper values for summary
  const fileDurationSeconds: number | null =
    (analysisData as any)?.videoDurationSeconds ?? 
    (analysisData as any)?.files?.find((f: any) => f.duration)?.duration ?? 
    null;
  const transcriptionText: string | null =
    (analysisData as any)?.transcription ?? null;
  const wordsInTranscript = transcriptionText
    ? transcriptionText.trim().split(/\s+/).length
    : null;
  const wordsPerMinute =
    fileDurationSeconds && wordsInTranscript
      ? Math.round((wordsInTranscript / Math.max(fileDurationSeconds, 1)) * 60)
      : null;

  // Context for explanations
  const analysisContext = {
    duration: fileDurationSeconds || 0,
    wordsPerMinute: wordsPerMinute || 0,
    intent: analysisData?.intentCategory || 'general',
    fillerCount: analysisData?.fillerWords?.length || 0,
  };

  //console.log('analysisContext -- - -', analysisContext);

  //(analysisData as any)?.files?.find((f: any) => f.name)?.name

  return (
    <MobileErrorBoundary onError={(error) => setComponentErrorWithTimeout(error.message)}>
      <div className="space-y-6">
             {/* Video Streaming - Fast encrypted video with audio */}
             {(() => {
               // Get the video file ID from analysis data - comprehensive check for video files
               const videoFile = (analysisData as any)?.files?.find((f: any) => {
                 // Skip audio-encrypted files
                 if (f.key?.includes('-audio-encrypted')) return false;
                 
                 // Check multiple conditions for video files:
                 // 1. Type starts with 'video/'
                 // 2. Key contains video extensions
                 // 3. Key doesn't contain 'audio' (audio-only files)
                 return (
                   f.type?.startsWith('video/') || 
                   f.key?.includes('.mp4') || 
                   f.key?.includes('.webm') || 
                   f.key?.includes('.mov')
                 );
               });
               
               // Debug log to see what files we have
               if (!videoFile) {
                 // If a direct videoUrl is provided (e.g. demo/share), render a standard video player
                 if (videoUrl) {
                   return (
                     <div className="space-y-4">
                       <div className="relative rounded-2xl overflow-hidden shadow-2xl bg-black">
                         <div className="relative aspect-video">
                           <video
                             src={videoUrl}
                             controls
                             playsInline
                             className="absolute inset-0 w-full h-full object-contain"
                           />
                           {/* Frame overlay matching Hero style */}
                           <div className="absolute inset-0 pointer-events-none">
                             <div className="absolute inset-0 border-[4px] border-white rounded-2xl"></div>
                           </div>
                         </div>
                       </div>
                     </div>
                   );
                 }

                 getLogger().warn('No video file found for streaming', {
                   metadata: {
                     totalFiles: (analysisData as any)?.files?.length || 0,
                     files: (analysisData as any)?.files?.map((f: any) => ({
                       id: f.id,
                       key: f.key,
                       type: f.type,
                       isAudioEncrypted: f.key?.includes('-audio-encrypted')
                     }))
                   }
                 });

                 return process.env.NODE_ENV === 'development' ? ( <div className="text-center py-8 text-muted-foreground">
                  <p><Trans>No video file available for streaming</Trans></p>
                  {process.env.NODE_ENV === 'development' && (
                    <p className="text-xs mt-2"><Trans>Files:</Trans> {(analysisData as any)?.files?.length || 0}</p>
                  )}
                </div>): null;


               }

              return !isMobile ? (
                <div className="space-y-4">
                  <FastVideoPlayer
                    fileId={videoFile.id}
                    thumbnailUrl={getThumbnailUrl(thumbnailKey || videoFile.thumbnailKey)}
                    title="Your Practice Video"
                    description="Fast encrypted streaming with audio"
                    showDownload={true}
                    muted={false}
                    autoPlay={false}
                  />
                  {/* <VideoDebug fileId={videoFile.id} /> */}
                </div>
              ) : null;
             })()}

        {/* Development Audio Player - Only visible in dev mode */}
        {/* <DevAudioPlayer analysisData={analysisData} /> */}

        {/* Component Error Display */}
        {componentError && (
          <Card className="border-2 border-brand-error/30 bg-gradient-to-br from-brand-error/5 to-transparent">
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-brand-error shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h3 className="text-sm font-bold text-brand-error mb-2">
                    <Trans>Component Error (Auto-dismisses in 1 minute)</Trans>
                  </h3>
                  <p className="text-sm text-brand-dark-blue font-medium mb-3">
                    {componentError}
                    {isMobile && (
                      <span className="block text-xs mt-1 text-brand-dark-blue/60">
                        <Trans>Mobile device detected - using optimized mode</Trans>
                      </span>
                    )}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => {
                        if (errorTimeoutId) {
                          clearTimeout(errorTimeoutId);
                          setErrorTimeoutId(null);
                        }
                        setComponentError(null);
                        getLogger().info('AIAnalysis error dismissed by user');
                      }}
                      size="sm"
                      variant="outline"
                      className="text-brand-error border-brand-error hover:bg-brand-error/5"
                    >
                      <Trans>Dismiss Now</Trans>
                    </Button>
                    <Button
                      onClick={() => {
                        if (errorTimeoutId) {
                          clearTimeout(errorTimeoutId);
                          setErrorTimeoutId(null);
                        }
                        setComponentError(null);
                        window.location.reload();
                      }}
                      size="sm"
                      className="bg-brand-error text-white hover:bg-brand-error/90"
                    >
                      <Trans>Reload Page</Trans>
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* ===== FILLER WORDS (always visible) ===== */}
      {!isAnalyzing && analysisData && Array.isArray(analysisData.fillerWords) && analysisData.fillerWords.length > 0 && (
        (() => {
          const fillerExplanation = getMetricExplanation('fillerWords', analysisData.fillerWords.length, analysisContext);
          const colors = getSeverityColors(fillerExplanation.severity);

          return (
            <Card className="border-2 border-brand-gold/30 pt-6">
              <CardContent>
                <div className={clsx("p-4 rounded-lg border-2 bg-gradient-to-br", colors.border, colors.background)}>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className={clsx("text-sm font-bold flex items-center gap-2", colors.title)}>
                      <AlertCircle className={clsx("h-4 w-4", colors.icon)} />
                      <Trans>Filler Words Used</Trans>
                    </h3>
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {analysisData.fillerWords.map((word, index) => (
                      <Badge key={index} className={clsx("font-medium border-opacity-20", colors.preview.replace('/15', '/10'), `border-${colors.icon.replace('text-', '')}`)} >
                        {word}
                      </Badge>
                    ))}
                  </div>
                  <div className={clsx("text-xs p-2 rounded", colors.preview)}>
                    <div className="flex items-start gap-1">
                      <Info className="h-3 w-3 mt-0.5 shrink-0" />
                      <span className="font-medium">
                        {fillerExplanation.explanation}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })()
      )}

      {/* ===== TRANSCRIPT (visible in partial + full) ===== */}
      {isSectionVisible('partial') && layout === 'data-first' && (
        <Card id="text">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              <Trans>Speech</Trans>
            </CardTitle>
            <CardDescription>
              <Trans>Full text with filler word highlights</Trans>
            </CardDescription>
          </CardHeader>
          <CardContent>
            {transcriptionText ? (
              <>
                <div className="max-h-96 overflow-auto whitespace-pre-wrap text-sm leading-6 mb-4 border rounded-lg p-4 bg-slate-50">
                  {(() => {
                    const fillerSet = new Set<string>(
                      ((analysisData as any)?.fillerWords as
                        | string[]
                        | undefined) ?? []
                    );
                    return transcriptionText.split(/(\s+)/).map((token, idx) => {
                      const isSpace = /\s+/.test(token);
                      if (isSpace) return <span key={idx}>{token}</span>;
                      const isFiller = fillerSet.has(
                        token.replace(/[^A-Za-z']/g, '').toLowerCase()
                      );
                      return (
                        <span
                          key={idx}
                          className={
                            isFiller
                              ? 'bg-yellow-100 text-yellow-900 rounded px-0.5'
                              : ''
                          }
                        >
                          {token}
                        </span>
                      );
                    });
                  })()}
                </div>
                <ScriptImprover
                  originalScript={transcriptionText}
                  improvedScript={analysisData?.improvedScript}
                  intent={analysisData?.intentCategory || 'general'}
                  analysisId={analysisData?.id}
                  title={(analysisData as any)?.files?.find((f: any) => f.name)?.name}
                  readOnly={readOnly}
                />
              </>
            ) : (
              <div className="text-sm text-muted-foreground">
                <Trans>No transcript available</Trans>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== SPEECH ANALYSIS / TIPS (visible in partial + full) ===== */}
      {isSectionVisible('partial') && (
        <Card id="sound" className="border-2 border-brand-gold/30 pt-8">
          <CardContent>
            {isAnalyzing ? (
              <div className="space-y-4 text-center py-8">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                <p className="text-muted-foreground"><Trans>Analyzing your speech...</Trans></p>
              </div>
            ) : analysisData && Array.isArray(analysisData.fillerWords) && Array.isArray(analysisData.personalizedTips) ? (
              <div className="space-y-6">
                {/* Personalized Tips */}
                {Array.isArray(analysisData.personalizedTips) && analysisData.personalizedTips.length > 0 && (
                  <div className="p-4 rounded-lg border-2 border-brand-success/20 bg-gradient-to-br from-brand-success/5 to-transparent">
                    <h3 className="text-sm font-bold mb-3 text-brand-dark-blue flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-brand-success" />
                      <Trans>Improvement Tips</Trans>
                    </h3>
                    <ul className="space-y-3">
                      {analysisData.personalizedTips.slice(0, isPartial ? 2 : undefined).map((tip, index) => (
                        <li key={index} className="flex items-start gap-3">
                          <CheckCircle className="h-5 w-5 text-brand-success shrink-0 mt-0.5" />
                          <span className="text-sm text-brand-dark-blue font-medium leading-relaxed">{tip}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Trans>No analysis data available</Trans>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ===== PREMIUM FEATURES (full access only) ===== */}
      {reportAccess === 'full' && analysisData && (
        <div className="space-y-4">
          <HiringManagerSummary
            analysisData={{
              ...analysisData,
              videoDurationSeconds: fileDurationSeconds ?? undefined,
            }}
            isLocked={false}
          />
          <ReadinessScore
            analysisData={{
              ...analysisData,
              videoDurationSeconds: fileDurationSeconds ?? undefined,
            }}
            isLocked={false}
          />
          <NextAttemptFocus
            analysisData={{
              ...analysisData,
              videoDurationSeconds: fileDurationSeconds ?? undefined,
            }}
            isLocked={false}
          />
        </div>
      )}

      {/* ===== PREMIUM FEATURES TEASER (partial access - show locked) ===== */}
      {reportAccess === 'partial' && analysisData && (
        <div className="space-y-4">
          <HiringManagerSummary
            analysisData={{
              ...analysisData,
              videoDurationSeconds: fileDurationSeconds ?? undefined,
            }}
            isLocked={true}
            onUnlock={onGetCreditsClick || onUnlockClick}
          />
          <ReadinessScore
            analysisData={{
              ...analysisData,
              videoDurationSeconds: fileDurationSeconds ?? undefined,
            }}
            isLocked={true}
            onUnlock={onGetCreditsClick || onUnlockClick}
          />
          <NextAttemptFocus
            analysisData={{
              ...analysisData,
              videoDurationSeconds: fileDurationSeconds ?? undefined,
            }}
            isLocked={true}
            onUnlock={onGetCreditsClick || onUnlockClick}
          />
        </div>
      )}

      {/* ===== PAYWALL BANNER (shown when not full access) ===== */}
      {reportAccess !== 'full' && (
        <PaywallBanner
          practicesRemaining={practicesRemaining}
          isUnlocking={isUnlocking}
          onUnlockWithCredit={onUnlockWithCredit}
          onGetCredits={onGetCreditsClick || onUnlockClick}
        />
      )}

      {/* ===== TEASER: blurred start of next locked section ===== */}
      {reportAccess !== 'full' && firstLockedSection && (
        <TeaserSection>
          {firstLockedSection.key === 'transcript' && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  <Trans>Speech</Trans>
                </CardTitle>
                <CardDescription>
                  <Trans>Full text with filler word highlights</Trans>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="h-4 bg-slate-200 rounded w-full" />
                  <div className="h-4 bg-slate-200 rounded w-5/6" />
                  <div className="h-4 bg-slate-200 rounded w-3/4" />
                </div>
              </CardContent>
            </Card>
          )}
          {firstLockedSection.key === 'speechAnalysis' && (
            <Card className="border-2 border-brand-gold/30 pt-8">
              <CardContent>
                <div className="p-4 rounded-lg border-2 border-brand-success/20 bg-gradient-to-br from-brand-success/5 to-transparent">
                  <h3 className="text-sm font-bold mb-3 text-brand-dark-blue flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-brand-success" />
                    <Trans>Improvement Tips</Trans>
                  </h3>
                  <div className="space-y-3">
                    <div className="h-4 bg-slate-200 rounded w-3/4" />
                    <div className="h-4 bg-slate-200 rounded w-1/2" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
          {firstLockedSection.key === 'questionFeedback' && (
            <Card className="border-2 border-brand-dark-blue/30">
              <CardHeader className="bg-gradient-to-r from-brand-dark-blue/5 to-brand-gold/5">
                <CardTitle className="flex items-center gap-2 text-brand-dark-blue font-bold">
                  <MessageSquare className="h-6 w-6 text-brand-dark-blue" />
                  <Trans>Interview Question Feedback</Trans>
                </CardTitle>
                <CardDescription className="text-brand-dark-blue/70 font-medium">
                  <Trans>Detailed analysis for each interview question</Trans>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 pt-4">
                  <div className="h-4 bg-slate-200 rounded w-3/4" />
                  <div className="h-4 bg-slate-200 rounded w-1/2" />
                  <div className="h-4 bg-slate-200 rounded w-2/3" />
                </div>
              </CardContent>
            </Card>
          )}
          {firstLockedSection.key === 'videoSetting' && (
            <Card>
              <CardHeader>
                <CardTitle><Trans>Visual Setting</Trans></CardTitle>
                <CardDescription>
                  <Trans>AI analysis of your video setup, lighting, framing, and background</Trans>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-24 bg-slate-100 rounded-lg" />
              </CardContent>
            </Card>
          )}
          {firstLockedSection.key === 'audioSetting' && (
            <Card>
              <CardHeader>
                <CardTitle><Trans>Audio Analysis</Trans></CardTitle>
                <CardDescription>
                  <Trans>AI analysis of your voice pitch, tone, volume, and speaking characteristics</Trans>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-24 bg-slate-100 rounded-lg" />
              </CardContent>
            </Card>
          )}
        </TeaserSection>
      )}

      {/* ===== QUESTION FEEDBACK (full only) ===== */}
      {reportAccess === 'full' && analysisData && analysisData.questionFeedback && Array.isArray(analysisData.questionFeedback) && analysisData.questionFeedback.length > 0 && (
        <Card id="question-feedback" className="border-2 border-brand-dark-blue/30">
          <CardHeader className="bg-gradient-to-r from-brand-dark-blue/5 to-brand-gold/5">
            <CardTitle className="flex items-center gap-2 text-brand-dark-blue font-bold">
              <MessageSquare className="h-6 w-6 text-brand-dark-blue" />
              <Trans>Interview Question Feedback</Trans>
            </CardTitle>
            <CardDescription className="text-brand-dark-blue/70 font-medium">
              <Trans>Detailed analysis for each interview question</Trans>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6 pt-6">
            {analysisData.questionFeedback.map((qf, index) => (
              <div key={index} className="p-5 rounded-xl border-2 border-gray-200 bg-gradient-to-br from-white to-slate-50/50 space-y-4">
                {/* Question */}
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-brand-dark-blue text-white flex items-center justify-center font-bold text-sm">
                    {index + 1}
                  </div>
                  <p className="text-brand-dark-blue font-semibold text-base leading-relaxed flex-1">
                    {qf.question}
                  </p>
                </div>

                {/* Strengths */}
                {qf.strengths && qf.strengths.length > 0 && (
                  <div className="p-4 rounded-lg border-2 border-brand-success/30 bg-gradient-to-br from-brand-success/5 to-transparent">
                    <h4 className="text-sm font-bold mb-2 text-brand-success flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      <Trans>Strengths</Trans>
                    </h4>
                    <ul className="space-y-2">
                      {qf.strengths.map((strength, sIndex) => (
                        <li key={sIndex} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-brand-success shrink-0 mt-0.5" />
                          <span className="text-sm text-brand-dark-blue leading-relaxed">{strength}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Weaknesses */}
                {qf.weaknesses && qf.weaknesses.length > 0 && (
                  <div className="p-4 rounded-lg border-2 border-red-300 bg-gradient-to-br from-red-50 to-transparent">
                    <h4 className="text-sm font-bold mb-2 text-red-600 flex items-center gap-2">
                      <AlertCircle className="h-4 w-4" />
                      <Trans>Areas for Improvement</Trans>
                    </h4>
                    <ul className="space-y-2">
                      {qf.weaknesses.map((weakness, wIndex) => (
                        <li key={wIndex} className="flex items-start gap-2">
                          <AlertCircle className="h-4 w-4 text-red-500 shrink-0 mt-0.5" />
                          <span className="text-sm text-brand-dark-blue leading-relaxed">{weakness}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Key Suggestion */}
                {qf.suggestion && (
                  <div className="p-4 rounded-lg border-2 border-brand-gold/30 bg-gradient-to-br from-brand-gold/5 to-transparent">
                    <h4 className="text-sm font-bold mb-2 text-brand-gold flex items-center gap-2">
                      <Star className="h-4 w-4" />
                      <Trans>Key Suggestion</Trans>
                    </h4>
                    <p className="text-sm text-brand-dark-blue leading-relaxed font-medium">
                      {qf.suggestion}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* ===== VIDEO SETTING (full only) ===== */}
      {reportAccess === 'full' && (
        <div key={`video-setting-${(analysisData as any)?.id || 'default'}`}>
          {(() => {
            try {
              return <VideoSetting analysisData={analysisData} thumbnailKey={thumbnailKey} demoThumbnailUrl={demoThumbnailUrl} />;
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : String(error);
              getLogger().error('AIAnalysis VideoSetting render error', {
                metadata: {
                  error: errorMessage,
                  stack: error instanceof Error ? error.stack : undefined,
                  analysisId: (analysisData as any)?.id
                }
              });
              return (
                <Card className="border-2 border-brand-error/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-brand-error">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        <Trans>Video Setting Analysis failed to load:</Trans> {errorMessage}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            }
          })()}
        </div>
      )}

      {/* ===== AUDIO SETTING (full only) ===== */}
      {reportAccess === 'full' && (
        <div key={`audio-setting-${(analysisData as any)?.id || 'default'}`}>
          {(() => {
            try {
              return <AudioSetting analysisData={analysisData} primaryFileId={primaryFileId} />;
            } catch (error) {
              const errorMessage = error instanceof Error ? error.message : String(error);
              getLogger().error('AIAnalysis AudioSetting render error', {
                metadata: {
                  error: errorMessage,
                  stack: error instanceof Error ? error.stack : undefined,
                  analysisId: (analysisData as any)?.id
                }
              });
              return (
                <Card className="border-2 border-brand-error/30">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-brand-error">
                      <AlertCircle className="h-4 w-4" />
                      <span className="text-sm font-medium">
                        <Trans>Audio Analysis failed to load:</Trans> {errorMessage}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            }
          })()}
        </div>
      )}
      </div>
    </MobileErrorBoundary>
  );
}